
import React, { useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { FeedbackFilters } from './components/FeedbackFilters';
import { FeedbackTable } from './components/FeedbackTable';
import { TicketDetailDialog } from './components/TicketDetailDialog';
import { AccessDeniedCard } from './components/AccessDeniedCard';
import { useAdminStatus } from './hooks/useAdminStatus';
import { useFeedbackTickets } from './hooks/useFeedbackTickets';
import { FeedbackTicket } from './types';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const FeedbackAdmin: React.FC = () => {
  const { isAdmin, loading: checkingAdmin } = useAdminStatus();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<FeedbackTicket | null>(null);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);

  const {
    tickets,
    loading,
    error,
    refreshTickets,
    updateTicketStatus,
  } = useFeedbackTickets();

  const updateStatus = async (id: string, status: 'Open' | 'In Progress' | 'Resolved'): Promise<boolean> => {
    try {
      const result = await updateTicketStatus(id, status);
      return result;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      return false;
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // Small delay to avoid UI glitches
      setTimeout(() => {
        setSelectedTicket(null);
      }, 300);
    }
  };

  const filteredTickets = tickets.filter((ticket: FeedbackTicket) => {
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || ticket.category === categoryFilter;
    const matchesSearch =
      searchQuery === '' ||
      ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ticket.user_info?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      (ticket.user_info?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

    return matchesStatus && matchesCategory && matchesSearch;
  });

  const handleTicketClick = (ticket: FeedbackTicket) => {
    setSelectedTicket(ticket);
  };

  if (checkingAdmin) {
    return (
      <div className="container py-10">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <AccessDeniedCard />;
  }

  return (
    <div className="container max-w-6xl py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2 inline-flex items-center gap-2">
          Feedback Administration
          <CircleHelp 
            className="h-5 w-5 text-primary cursor-pointer hover:text-primary/80 transition-colors" 
            onClick={() => setHelpOpen(true)}
            strokeWidth={2} 
          />
        </h1>
        <p className="text-muted-foreground">
          Manage and respond to user feedback submissions
        </p>
      </div>

      <FeedbackFilters
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <FeedbackTable
        tickets={filteredTickets}
        loading={loading}
        error={error}
        onTicketClick={handleTicketClick}
      />

      <TicketDetailDialog
        ticket={selectedTicket}
        onOpenChange={handleDialogOpenChange}
        onStatusChange={updateStatus}
        refreshTickets={refreshTickets}
      />
      
      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Feedback Administration Help</DialogTitle>
            <DialogDescription>
              Manage user feedback and support requests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p>Welcome to the Feedback Administration area! Here you can:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>View all user-submitted feedback and requests</li>
              <li>Filter tickets by status and category</li>
              <li>Update the status of tickets</li>
              <li>Respond to user inquiries</li>
              <li>Track resolution progress</li>
            </ul>
            <p>Use the filters above to quickly find specific tickets in the system.</p>
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="default" size="sm">Got it</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackAdmin;
